import { ReactNode } from 'react';
import { Container } from '@mui/material';
import { motion } from 'framer-motion';

export interface AiFeature {
  title: string;
  desc: string;
}

export interface AiSectionProps {
  /** 코너 글로우 색상 로테이션용 인덱스 */
  index: number;
  eyebrow: string;
  title: string;
  titleAccent: string;
  descriptions: string[];
  tags?: string[];
  /** Agentgateway 주요 기능 등 항목 리스트 */
  features?: AiFeature[];
  /** 우측(또는 좌측) 애니메이션 다이어그램 */
  visual: ReactNode;
  /** 좌우 배치 반전 */
  reverse?: boolean;
}

const reveal = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

/**
 * AI 랜딩 개별 주제 섹션.
 * 좌측 텍스트(+태그/기능목록) / 우측 애니메이션 다이어그램의 2단 레이아웃.
 * 스크롤로 뷰포트 진입 시 framer-motion 으로 페이드·슬라이드 인.
 */
export default function AiSection(props: AiSectionProps) {
  const { index, eyebrow, title, titleAccent, descriptions, tags, features, visual, reverse } = props;

  return (
    <section className={`ai-section ai-section--g${index % 5}`}>
      <Container maxWidth="xl">
        <div className={`ai-section__inner ${reverse ? 'ai-section__inner--reverse' : ''}`}>
          <motion.div
            className="ai-section__text"
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="ai-section__eyebrow">{eyebrow}</div>
            <h2 className="ai-section__title">
              {title}
              <br />
              <span className="ai-accent">{titleAccent}</span>
            </h2>
            <div className="ai-section__desc">
              {descriptions.map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>

            {tags && tags.length > 0 && (
              <div className="ai-section__tags">
                {tags.map((tag) => (
                  <span key={tag} className="ai-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {features && features.length > 0 && (
              <div className="ai-section__features">
                {features.map((f) => (
                  <div key={f.title} className="ai-feature">
                    <span className="ai-feature__dot" />
                    <div>
                      <b className="ai-feature__title">{f.title}</b>
                      <span className="ai-feature__desc">{f.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            className="ai-section__visual"
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            {visual}
          </motion.div>
        </div>
      </Container>
    </section>
  );
}
